import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, Instagram, Mail, Loader2, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    sujet: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Message envoyé !");
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
            Contact
          </p>
          <h1 className="page-title animate-fade-in-up animation-delay-100">
            Me contacter
          </h1>
          <p className="page-subtitle animate-fade-in-up animation-delay-200">
            Une question ? Une envie de projet ? N'hésitez pas à m'écrire.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Contact Info */}
            <div>
              <h2 className="text-xl font-heading mb-8">Informations</h2>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Atelier La Peauésie</h3>
                    <p className="text-muted-foreground text-sm">
                      9 quai Ernest de Franqueville<br />
                      85100 Les Sables-d'Olonne<br />
                      France
                    </p>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Instagram</h3>
                    <a
                      href="https://instagram.com/nymphea.ttt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground text-sm hover:text-foreground transition-colors"
                      data-testid="contact-instagram"
                    >
                      @nymphea.ttt
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">
                      Pour voir mes dernières créations
                    </p>
                  </div>
                </div>

                {/* TikTok */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">TikTok</h3>
                    <a
                      href="https://www.tiktok.com/@nymphea.ttt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground text-sm hover:text-foreground transition-colors"
                      data-testid="contact-tiktok"
                    >
                      @nymphea.ttt
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">
                      Pour découvrir mes vidéos
                    </p>
                  </div>
                </div>

                {/* Email hint */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 border border-border flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Email</h3>
                    <p className="text-muted-foreground text-sm">
                      Utilisez le formulaire ci-contre ou contactez-moi via Instagram pour une réponse rapide.
                    </p>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="mt-12 p-6 bg-secondary/50 border border-border/40">
                <h3 className="font-medium mb-2">Horaires</h3>
                <p className="text-sm text-muted-foreground">
                  L'Atelier La Peauésie est ouvert du lundi au samedi de 10h à 18h. Je réponds aux messages généralement sous 48h.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {success ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" strokeWidth={1} />
                  <h2 className="text-xl font-heading mb-2">Message envoyé !</h2>
                  <p className="text-muted-foreground">
                    Merci pour votre message. Je vous répondrai dès que possible.
                  </p>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setFormData({ nom: "", email: "", sujet: "", message: "" });
                    }}
                    variant="outline"
                    className="mt-6 border-foreground text-foreground hover:bg-foreground hover:text-background"
                  >
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <Label htmlFor="nom" className="form-label">Nom / Prénom *</Label>
                      <Input
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="contact-nom"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="email" className="form-label">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="sujet" className="form-label">Sujet *</Label>
                    <Input
                      id="sujet"
                      name="sujet"
                      value={formData.sujet}
                      onChange={handleChange}
                      required
                      placeholder="Question, demande d'info..."
                      className="form-input"
                      data-testid="contact-sujet"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="message" className="form-label">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Votre message..."
                      className="form-textarea min-h-[180px]"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs w-full"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      "Envoyer le message"
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
